/**
 * Room Art: the design hung on the wall of three room scenes (gallery slides 2–4), drawn at the real
 * scale of the selected size, as Canvas Print, Framed Canvas or Framed Print in the chosen frame colour.
 * Framed Print (Jondo MPP, owner 2026-10-19): the finished piece is the print + 2″ (a 1″ moulding each side, e.g.
 * 16×20 → 18×22); inside it the print sheet as c365-mockup.js printImage() lays it out — the mat and the design
 * exactly as the print file, or the design to the edge for No Mat — under a faint acrylic sheen.
 *
 * Data (snippets/c365-room-art.liquid → [data-room-json]):
 *   sizes  {size: [scene handle ×3]}          product c365.room_scenes — rooms change with the size
 *   scenes {handle: {img, ppw, ax, by, cy, top, maxw, light}}   shop c365.room_library
 *   images {variantId: {size | '*': art url}} variant c365.room_images (flat front art, bleed cut)
 * ppw = share of the scene width per inch of wall; ax/by/cy/top = fractions of the (square) scene.
 * Placement mirrors migration/room-art/build_scenes.py place().
 *
 * Listens to `c365:change` from <c365-product> (size / type / frame / variant) and to `c365:preview`
 * ({detail: {image}}) from the Personalize tool, which swaps the art for the customer's own preview.
 */
// Same frame spec as the 3/4 product mockups (tools/mockups/render.py): floating frame, 1.5″ deep.
const FRAME_FACE = 0.5; // inches, frame face
const FRAME_GAP = 0.3; // inches, dark reveal between canvas and frame
const SHADOW = {
  canvas: { x: 0.45, y: 0.8, blur: 1.4 }, // 1.25″ deep
  framed: { x: 0.6, y: 1.0, blur: 1.8 }, // floating frame, 1.5″ deep
  print: { x: 0.35, y: 0.6, blur: 1.1 }, // Framed Print moulding, 3/4″ deep
};
const FP_FACE = 1; // inches, Framed Print moulding per side (finished size = print + 2″)
// Liquid's t filter HTML-escapes ' " & < >; the strings here go to textContent / template literals, so undo it
const D = (s) => (typeof s === 'string' ? s.replace(/&(#39|#34|quot|amp|lt|gt);/g, (m, e) => ({ '#39': "'", '#34': '"', quot: '"', amp: '&', lt: '<', gt: '>' })[e]) : s);
// Captions in the visitor's language: window.C365_T (c365.js.product.room_* and "label.<English value>", filled by
// sections/c365-product.liquid and snippets/c365-room-art.liquid), English without it.
const T = (k, f, vars) =>
  String(D(window.C365_T && window.C365_T[k]) || f).replace(/\{(\w+)\}/g, (m, n) => (vars && n in vars ? vars[n] : m));
const L = (s) => D(s && window.C365_T && window.C365_T[`label.${s}`]) || s;

function frameKind(name) {
  const v = (name || '').toLowerCase();
  if (v.includes('rustic')) return 'rustic'; // "Rustic White" (Framed Print only)
  if (v.includes('white')) return 'white';
  if (v.includes('walnut')) return 'walnut';
  if (v.includes('natural') || v.includes('oak')) return 'natural';
  if (v.includes('gold')) return 'gold';
  if (v.includes('silver')) return 'silver';
  return 'black';
}

function sized(url, width) {
  if (!url || url.startsWith('data:') || url.startsWith('blob:')) return url;
  try {
    const u = new URL(url, location.href);
    u.searchParams.set('width', String(width));
    return u.toString();
  } catch {
    return url;
  }
}

class RoomArt {
  constructor(pdp, data) {
    this.pdp = pdp;
    this.data = data;
    this.preview = null;
    this.slots = Array.from(pdp.querySelectorAll('[data-room-slot]'));
    pdp.addEventListener('c365:change', () => this.update());
    document.addEventListener('c365:preview', (e) => {
      this.preview = e.detail?.image || null;
      this.update();
    });
    this.update();
  }

  /** Flat art for the current variant + size: exact size, then same ratio, then any. */
  artUrl(size) {
    if (this.preview) return this.preview;
    const all = this.data.images;
    const imgs = all[String(this.pdp.variant?.id)] || Object.values(all).find((v) => v && Object.keys(v).length) || {};
    if (imgs[size]) return imgs[size];
    const [w, h] = size.split('x').map(Number);
    const same = Object.keys(imgs).find((k) => {
      const [a, b] = k.split('x').map(Number);
      return a && b && Math.abs(a / b - w / h) < 0.02;
    });
    return imgs[same] || imgs['*'] || Object.values(imgs)[0] || '';
  }

  place(sc, w, h, framed, print) {
    const m = print ? 2 * FP_FACE : framed ? 2 * (FRAME_FACE + FRAME_GAP) : 0;
    const fw = (w + m) * sc.ppw;
    const fh = (h + m) * sc.ppw;
    let top = sc.cy != null ? Math.min(sc.cy - fh / 2, sc.by - fh) : sc.by - fh;
    top = Math.max(top, Math.min(sc.top, sc.by - fh));
    const left = Math.min(Math.max(sc.ax - fw / 2, 0.01), 0.99 - fw);
    return { left, top, fw, fh };
  }

  update() {
    const size = this.pdp.size;
    if (!size) return;
    const handles = this.data.sizes[size] || [];
    const [w, h] = size.split('x').map(Number);
    const print = this.pdp.isPrint;
    const framed = this.pdp.hasFrame;
    const kind = framed ? frameKind(this.pdp.frame) : '';
    const flat = this.artUrl(size);
    const matted = print && this.pdp.mat !== 'No Mat';
    // Framed Print: the print sheet (mat + design as the print file), drawn by c365-mockup.js; the flat art shows
    // until it is ready, and a stale result (another size / mat / design meanwhile) is dropped
    let art = flat;
    if (print && flat) {
      const key = `${flat}|${size}|${matted}|${this.preview || ''}|${this.pdp.themeId || ''}`;
      if (this.sheetKey === key && this.sheet) art = this.sheet;
      else if (this.sheetKey !== key && this.pdp.data?.mockupJs) {
        this.sheetKey = key;
        this.sheet = null;
        import(this.pdp.data.mockupJs)
          .then(({ printImage }) => printImage(flat, size, matted, this.preview, this.pdp.themeId || null))
          .then((url) => {
            if (this.sheetKey !== key) return;
            this.sheet = url;
            this.update();
          })
          .catch((err) => console.warn('[c365] room art print sheet', err));
      }
    }
    const type = L(print ? 'Framed Print' : 'Framed Canvas');
    const frameName = L(this.pdp.frame);
    const finished = print
      ? ` · ${T('product.room_finished', '{size}″ finished', { size: `${w + 2 * FP_FACE}×${h + 2 * FP_FACE}` })}`
      : '';
    const what = print ? `${type} · ${frameName} · ${L((this.pdp.mat || '').replace('"', '″'))}` : `${type} · ${frameName}`;
    const label = `${size.replace('x', '×')}″ ${framed ? what : L('Canvas Print')}${finished}`;

    this.slots.forEach((el) => {
      const k = Number(el.dataset.roomSlot);
      const sc = this.data.scenes[handles[k]] || this.data.scenes[handles[0]];
      el.hidden = !sc || !art;
      el.closest('[data-room-item]')?.toggleAttribute('hidden', el.hidden);
      if (el.hidden) return;
      const thumb = el.hasAttribute('data-room-thumb');
      const bg = el.querySelector('[data-room-bg]');
      const src = sized(sc.img, thumb ? 240 : 1600);
      if (bg.dataset.src !== src) {
        bg.dataset.src = src;
        if (!thumb) {
          bg.srcset = [600, 900, 1200, 1600].map((x) => `${sized(sc.img, x)} ${x}w`).join(', ');
        }
        bg.src = src;
      }
      const p = this.place(sc, w, h, framed, print);
      const piece = el.querySelector('[data-room-piece]');
      const dir = sc.light === 'right' ? -1 : 1;
      const cq = (inches) => `${(inches * sc.ppw * 100).toFixed(3)}cqw`;
      Object.assign(piece.style, {
        left: `${(p.left * 100).toFixed(3)}%`,
        top: `${(p.top * 100).toFixed(3)}%`,
        width: `${(p.fw * 100).toFixed(3)}%`,
        height: `${(p.fh * 100).toFixed(3)}%`,
      });
      const sh = SHADOW[print ? 'print' : framed ? 'framed' : 'canvas'];
      piece.style.setProperty('--sx', cq(sh.x * dir));
      piece.style.setProperty('--sy', cq(sh.y));
      piece.style.setProperty('--sb', cq(sh.blur));
      piece.style.setProperty('--face', cq(print ? FP_FACE : FRAME_FACE));
      piece.style.setProperty('--inset', cq(print ? FP_FACE : FRAME_FACE + FRAME_GAP));
      piece.dataset.roomFrame = kind; // not data-frame: c365-product.js hides every [data-frame] that isn't an offered frame swatch
      piece.classList.toggle('is-framed', framed && !print);
      piece.classList.toggle('is-print', print);
      const img = piece.querySelector('[data-room-img]');
      const artSrc = sized(art, thumb ? 240 : Math.min(1600, Math.ceil(p.fw * 1600 * 2)));
      if (img.dataset.src !== artSrc) {
        img.dataset.src = artSrc;
        img.src = artSrc;
      }
      el.setAttribute('aria-label', T('product.room_aria', '{label} shown to scale in a room', { label }));
      const cap = el.querySelector('[data-room-cap]');
      if (cap) cap.textContent = T('product.room_caption', '{label} · shown to scale', { label });
      // Close-up of the frame corner (the frame is true to scale, so it is thin in a whole-room view).
      const detail = el.querySelector('[data-room-detail]');
      if (detail) {
        const src = print ? this.data.printFrameImgs?.[kind] : framed ? this.data.frameImgs?.[kind] : '';
        detail.hidden = !src;
        if (src) {
          const img = detail.querySelector('img');
          if (img.getAttribute('src') !== src) img.src = src;
          detail.querySelector('span').textContent = this.pdp.isPrint
            ? T('product.room_frame', '{frame} frame', { frame: frameName })
            : T('product.room_floating_frame', '{frame} floating frame', { frame: frameName });
        }
      }
    });
  }
}

customElements.whenDefined('c365-product').then(() => {
  document.querySelectorAll('c365-product').forEach((pdp) => {
    const json = pdp.querySelector('[data-room-json]');
    if (!json) return;
    try {
      new RoomArt(pdp, JSON.parse(json.textContent));
    } catch (err) {
      console.warn('[c365] room art', err);
    }
  });
});
